import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ComponentProps } from 'react';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { EdgeRectangleTrapezoid } from '@sorare/core/src/atoms/ui/EdgeRectangleTrapezoid';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { isType } from '@sorare/core/src/lib/gql';

import { DumbTask } from '../DumbTask';
import { Colors } from '../getTaskColors';
import { ClaimButton } from './ClaimButton';
import type { UnclaimedTask_taskInterface } from './__generated__/index.graphql';

const NoBottomPaddingDumbTask = styled(DumbTask)`
  &&.noBottomPadding {
    padding-bottom: 0;
  }
`;
const AlignCenter = styled.div`
  align-self: center;
`;
const Footer = styled(Vertical).attrs({ gap: 2 })`
  justify-content: space-between;
  flex: 1;
`;
const FullWidthHorizontal = styled(Horizontal)`
  width: 100%;
  & > :first-child {
    flex: 1;
  }
`;

type Props = {
  unclaimedTask: UnclaimedTask_taskInterface;
  featured?: boolean;
  borderColor?: ComponentProps<typeof DumbTask>['borderColor'];
  onClaim?: () => void;
  loading?: boolean;
};
export const UnclaimedTask = ({
  unclaimedTask,
  featured,
  borderColor,
  onClaim,
  loading,
}: Props) => {
  const { iconUrl, title, color, description, rewardConfigs } = unclaimedTask;

  return (
    <NoBottomPaddingDumbTask
      className={classNames({ noBottomPadding: !featured })}
      borderColor={borderColor}
      color={
        isType(unclaimedTask, 'ThresholdsDuoConnectionTask')
          ? Colors.BLUE
          : undefined
      }
      forceVertical
      icon={!!iconUrl && <img width={24} src={iconUrl} alt="" />}
      title={<LabelL bold>{title}</LabelL>}
      description={
        isType(unclaimedTask, 'ThresholdsDuoConnectionTask') ? (
          <Horizontal>
            <Avatar user={unclaimedTask.connectedUser} rounded size={2} />
            <LabelM bold>
              <UserName user={unclaimedTask.connectedUser} />
            </LabelM>
          </Horizontal>
        ) : (
          <BodyS>
            <Ellipsis visibleLines={2}>{description}</Ellipsis>
          </BodyS>
        )
      }
      footer={
        featured ? (
          <FullWidthHorizontal>
            <ClaimButton
              task={unclaimedTask}
              onClaim={onClaim}
              loading={loading}
            />
            {rewardConfigs.map(rewardConfig => (
              <AnyRewardConfig
                key={rewardConfig.id}
                anyRewardConfig={rewardConfig}
                variant={Variant.CHIP}
              />
            ))}
          </FullWidthHorizontal>
        ) : (
          <Footer>
            <ClaimButton
              task={unclaimedTask}
              onClaim={onClaim}
              loading={loading}
            />
            {rewardConfigs.length > 0 && (
              <AlignCenter>
                <EdgeRectangleTrapezoid
                  padding="var(--half-unit) var(--triple-unit)"
                  border={`linear-gradient(to bottom, ${color} 0%, #111F1C 70%)`}
                >
                  {rewardConfigs.map(rewardConfig => (
                    <AnyRewardConfig
                      key={rewardConfig.id}
                      anyRewardConfig={rewardConfig}
                      variant={Variant.TEXT}
                    />
                  ))}
                </EdgeRectangleTrapezoid>
              </AlignCenter>
            )}
          </Footer>
        )
      }
    />
  );
};

UnclaimedTask.fragments = {
  taskInterface: gql`
    fragment UnclaimedTask_taskInterface on TaskInterface {
      id
      iconUrl
      title
      color
      description
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ... on ThresholdsDuoConnectionTask {
        id
        connectedUser {
          slug
          ...Avatar_user
          ...UserName_publicUserInfoInterface
        }
      }
      ...ClaimButton_taskInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${ClaimButton.fragments.taskInterface}
    ${Avatar.fragments.user}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<UnclaimedTask_taskInterface>,
};
